import { config } from '@/config';
import useWindowWidth from '@/hooks/useWindowWidth';
import { useGetCheckoutProfileQuery } from '@/redux/query';
import { BlockStack, Grid, useBreakpoints } from '@shopify/polaris';
import { ClipboardCheckIcon, CodeIcon, ThemeEditIcon } from '@shopify/polaris-icons';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { DatePickerShow } from './components/DatePickerShow';
import { EmbedAppStatus } from './components/EmbedAppStatus';
import { ExtensionCard } from './components/ExtensionCard';
import SideNavMenu from './components/SideNavMenu';
import { StyledRightSidePosition } from './components/styled';
import { WidgetPlaceManually } from './components/WidgetPlaceManually';
import { BREAK_POINT_WIDTH_APPEARANCE } from '@/constants';
import { widgetSettingSelector } from '@/redux/slice/widget.slice';
import { useSelector } from 'react-redux';
import { DISPLAY_TYPE_OPTIONS, WIDGET_DISPLAY_CHOICE, WIDGET_POSITION_CHOICE } from '@/constants/enum';

const WidgetPosition = () => {
  const { data: checkoutProfileData, isLoading: isLoadingCheckoutProfile } = useGetCheckoutProfileQuery({ shop: config.shop });
  const [selectedTab, setSelectedTab] = useState('embed');
  const windowWidth = useWindowWidth();
  const widgetSettings = useSelector(widgetSettingSelector);

  const checkoutProfileOptions = useMemo(
    () =>
      checkoutProfileData?.data?.checkoutProfiles.map((item) => ({
        value: item.id?.split('/')?.at(-1)!,
        label: item.name,
      })),
    [checkoutProfileData],
  );

  const tabsConfig = useMemo(
    () => [
      {
        value: 'embed',
        label: 'Embed app',
        icon: CodeIcon,
        onClick: () => {
          const element = document.getElementById('embed');
          if (element) {
            const offset = -800;
            const top = element.getBoundingClientRect().top + window.scrollY + offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        },
      },
      {
        value: 'widget-position',
        label: 'Widget position',
        icon: ThemeEditIcon,
        onClick: () => {
          const element = document.getElementById('widget-position');
          if (element) {
            const offset = 223;
            const top = element.getBoundingClientRect().top + window.scrollY + offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        },
      },
      {
        value: 'postpurchase',
        label: 'Postpurchase',
        icon: ClipboardCheckIcon,
        onClick: () => {
          const element = document.getElementById('postpurchase');
          if (element) {
            const offset = -10;
            const top = element.getBoundingClientRect().top + window.scrollY + offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        },
      },
    ],
    [],
  );

  useEffect(() => {
    const sectionMap = [
      { id: 'embed', tab: 'embed', offsetTop: 800 },
      { id: 'widget-position', tab: 'widget-position', offsetTop: -223 },
      { id: 'postpurchase', tab: 'postpurchase', offsetTop: 10 },
    ];

    const onScroll = debounce(() => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.scrollHeight;

      if (scrollY + windowHeight >= documentHeight - 100) {
        setSelectedTab('postpurchase');
        return;
      }

      for (const section of sectionMap) {
        const el = document.getElementById(section.id);
        if (!el) continue;

        const top = el.getBoundingClientRect().top + scrollY;
        if (scrollY >= top - section.offsetTop) {
          setSelectedTab(section.tab);
        }
      }
    }, 100);

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const minHeightRight = useMemo(() => {
    if (widgetSettings.widgetPosition.displayOnPage.length === 2) {
      return '2250px';
    } else if (widgetSettings.widgetPosition.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT)) {
      return '1700px';
    } else if (widgetSettings.widgetPosition.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.CART)) {
      return '1910px';
    }
  }, [widgetSettings.widgetPosition.displayOnPage]);

  return (
    <Grid columns={{ lg: 12, md: 6, xs: 6 }} gap={{ xs: '400' }}>
      {windowWidth >= BREAK_POINT_WIDTH_APPEARANCE && (
        <Grid.Cell columnSpan={{ lg: 3, md: 2 }}>
          <SideNavMenu
            tabsConfig={tabsConfig}
            selectedTab={selectedTab}
            onLeave={(tab) => {
              setSelectedTab(tab);
            }}
            isChangeData={false}
          />
        </Grid.Cell>
      )}
      <Grid.Cell columnSpan={{ lg: 9, md: windowWidth >= BREAK_POINT_WIDTH_APPEARANCE ? 4 : 6, xs: 6 }}>
        <StyledRightSidePosition
          marginLeft={useBreakpoints().lgUp ? '32px' : '0'}
          minHeight={windowWidth >= BREAK_POINT_WIDTH_APPEARANCE ? minHeightRight : 'initial'}
        >
          <BlockStack gap={'500'}>
            <div id="embed">
              <EmbedAppStatus />
            </div>
            <div id="widget-position">
              <DatePickerShow />
            </div>
            <WidgetPlaceManually />
            <div id="postpurchase">
              <ExtensionCard
                isThankYouPage
                isLoading={isLoadingCheckoutProfile}
                checkoutProfileOptions={checkoutProfileOptions}
              />
            </div>
            <div id="account">
              <ExtensionCard isLoading={isLoadingCheckoutProfile} checkoutProfileOptions={checkoutProfileOptions} />
            </div>
          </BlockStack>
        </StyledRightSidePosition>
      </Grid.Cell>
    </Grid>
  );
};

export default WidgetPosition;
